/**
 * Toucan Reads — finding out what a saved link actually is. One item at a
 * time: pull the page through the proxy, decide its kind from what came back,
 * and write what was learnt onto the item. The decisions are pure functions in
 * `model.ts`; this file does the fetching, parsing and writing.
 */

import {
  BlobTooLargeError,
  fetchPage,
  putBlob,
  required,
  stated,
} from "toucan";
import { readPdf } from "./pdf.ts";
import {
  animatedPicture,
  canonicalUrl,
  concludeKind,
  countWords,
  forgetFetched,
  imageIsSiteWide,
  isBotChallenge,
  isGone,
  isPdfContent,
  isSettled,
  leadImage,
  mirrorOf,
  picturable,
  readMeta,
  refileItem,
  setItemFailed,
  setItemPublished,
  setItemMissing,
  setItemRefused,
  setItemUnframeable,
} from "./model.ts";
import type { Item, ItemKind, ReadsStore } from "./model.ts";

/**
 * Readability, loaded the first time a page needs extracting. It is the
 * heaviest thing the app loads, and a library of links that are all PDFs
 * never needs it at all.
 */
async function readability() {
  return import("@mozilla/readability");
}

/** What Readability made of a page, where it made anything. */
interface Extracted {
  text: string;
  html: string;
  /** The picture inside the article, where there is one. */
  image?: string;
  title?: string;
}

import DOMPurify from "dompurify";

/**
 * Elements past which a document is not a page with an article on it but an
 * application, and parsing one on a phone stalls it for an answer that would
 * be wrong anyway.
 */
export const MAX_PARSE_ELEMENTS = 30_000;

/**
 * Readability's own reading of the page, or null where it could not make one.
 * It aborts by throwing — on a document past the element cap, and on a few
 * malformed ones — which is an answer of "not an article" rather than a fault.
 * Readability rewrites the document it is given, so it gets a clone: the
 * caller still needs the original.
 */
function parseArticle(
  page: Document,
  Reader: (typeof import("@mozilla/readability"))["Readability"],
) {
  const copy = page.cloneNode(true) as Document;
  unmisleadPictures(copy);
  try {
    return new Reader(copy, {
      maxElemsToParse: MAX_PARSE_ELEMENTS,
    }).parse();
  } catch {
    return null;
  }
}

/** An attribute value that is a picture's file name, as Readability reads one. */
const PICTURE_FILE = /\.(?:jpg|jpeg|png|webp)(?:\s|$)/i;

/**
 * Take off a picture the attributes Readability would mistake for its
 * address. Its lazy-loading rule copies any attribute whose value looks like
 * a picture file into `src`, and National Geographic gives each picture an
 * `id` that is its file name, which would replace a good `src` with one that
 * resolves to nothing. A lazy loader keeps the real address in a `data-`
 * attribute or one with `src` in its name, so those stay.
 */
function unmisleadPictures(page: Document): void {
  for (const picture of page.querySelectorAll("img, picture, figure")) {
    for (const attribute of [...picture.attributes]) {
      const { name, value } = attribute;
      if (
        !name.startsWith("data-") &&
        !name.includes("src") &&
        name !== "alt" &&
        PICTURE_FILE.test(value)
      ) {
        picture.removeAttribute(name);
      }
    }
  }
}

/**
 * Extract the reading text, if the page has any. `isProbablyReaderable` is the
 * whole of the gate: word count, link density and `og:type` each fail to tell
 * an article from a homepage full of blurb.
 */
async function extract(
  page: Document,
  base: string,
): Promise<Extracted | null> {
  const { isProbablyReaderable, Readability } = await readability();
  if (!isProbablyReaderable(page)) {
    return null;
  }
  const article = parseArticle(page, Readability);
  if (article === null) {
    return null;
  }
  // The HTML is sanitised before anything downstream sees it, because
  // Readability removes <script> but leaves event-handler attributes and
  // javascript: addresses standing. This HTML is stored and synced, then
  // injected into the reading view with dangerouslySetInnerHTML.
  const extracted: Extracted = {
    // Readability fills both of these in for every article it returns; a
    // reading that came back without them is broken rather than empty.
    text: required(article.textContent, "article text"),
    html: required(
      DOMPurify.sanitize(required(article.content, "article html")),
      "article html",
    ),
  };
  const holder = page.createElement("div");
  holder.innerHTML = extracted.html;
  const image = leadImage(holder, base);
  if (image !== undefined) {
    extracted.image = image;
  }
  const title = stated(article.title);
  if (title !== undefined) {
    extracted.title = title;
  }
  return extracted;
}

/**
 * The picture a card points at. A URL already on `SAME_IMAGE_LIMIT` cards
 * counts as no picture, since a site-wide social card is the same address on
 * every page.
 */
function chooseThumb(
  store: ReadsStore,
  candidates: (string | undefined)[],
): string | undefined {
  for (const candidate of candidates) {
    if (
      candidate !== undefined &&
      // A page's own `og:image` reaches here as the page wrote it, unresolved,
      // so this is where it is checked to be a web address.
      picturable(candidate) &&
      !animatedPicture(candidate) &&
      !imageIsSiteWide(store, candidate)
    ) {
      return candidate;
    }
  }
  return undefined;
}

/**
 * What a page turned out to say, gathered before any of it is written down.
 *
 * A pass reads and stores everything first and writes it in one go, because
 * the write begins with `forgetFetched`. A pass cut off after that — a phone
 * frozen mid-fetch — would leave an item claiming a reading it has lost, with
 * the last `fetchedAt` telling every later sweep the question is closed.
 */
interface PageFacts {
  url: string;
  kind: ItemKind;
  /** Whether Readability found an article, for when the file isn't a PDF. */
  readable: boolean;
  fileUrl?: string;
  title?: string;
  publishedAt?: string;
  thumbUrl?: string;
  words?: number;
  textHash?: string;
  /** What went wrong with the page that came back (see `setItemFailed`). */
  failed?: string;
}

/**
 * A title with the `<br>`s a site escaped into its metadata read as the space
 * each stands for. Only `<br>`, since a title can name a tag on purpose.
 */
function unbroken(title: string | undefined): string | undefined {
  return title?.replace(/(\s*<br\s*\/?>\s*)+/gi, " ").trim();
}

/**
 * Everything an HTML page had to say.
 *
 * `readFrom` is where the bytes came from and `address` is the page's own
 * address. They differ only for a page read through a mirror, where the item
 * keeps its own address and the links inside resolve against the mirror.
 */
async function readHtml(
  store: ReadsStore,
  html: string,
  readFrom: string,
  address: string,
): Promise<PageFacts> {
  const page = new DOMParser().parseFromString(html, "text/html");
  const url = canonicalUrl(address, page);
  // A document parsed here has the app's address as its base, so without this
  // Readability would resolve the article's links and pictures against Toucan.
  const base = page.createElement("base");
  base.setAttribute("href", readFrom);
  page.head.prepend(base);
  const meta = readMeta(page);
  const article = await extract(page, readFrom);
  const title = unbroken(article?.title ?? meta.title);
  // The title goes in as evidence as well as onto the item: a viewer page
  // names the file it fronts, and that name is what says whether there is a
  // paper behind it.
  const readable = article !== null;
  const conclusion = concludeKind({
    url,
    readable,
    ...(title === undefined ? {} : { name: title }),
    ...(meta.pdfUrl === undefined ? {} : { pdfUrl: meta.pdfUrl }),
  });

  const facts: PageFacts = { url, kind: conclusion.kind, readable };
  if (conclusion.fileUrl !== undefined) {
    facts.fileUrl = conclusion.fileUrl;
  }
  if (title !== undefined) {
    facts.title = title;
  }
  if (meta.publishedAt !== undefined) {
    facts.publishedAt = meta.publishedAt;
  }
  // Chosen for every page, not only the ones that extracted: a shop's product
  // page has a picture and a name and no article.
  const thumb = chooseThumb(store, [article?.image, meta.image]);
  if (thumb !== undefined) {
    facts.thumbUrl = thumb;
  }

  if (article === null) {
    return facts;
  }
  facts.words = countWords(article.text);
  try {
    facts.textHash = await putBlob(new TextEncoder().encode(article.html));
  } catch (error) {
    // The card keeps what the document has and loses only the full text,
    // and says why the page is thin.
    console.error(
      `Toucan Reads: the text for ${url} could not be stored`,
      error,
    );
    facts.failed =
      error instanceof BlobTooLargeError
        ? "This page’s text is too large to store."
        : "This page’s text could not be stored.";
  }
  return facts;
}

/** Write down what a page said, in one go. */
function writePage(item: Item, facts: PageFacts): void {
  item.url = facts.url;
  item.kind = facts.kind;
  if (facts.fileUrl !== undefined) {
    item.fileUrl = facts.fileUrl;
  }
  if (facts.title !== undefined) {
    item.title = facts.title;
  }
  setItemPublished(item, facts.publishedAt);
  if (facts.thumbUrl !== undefined) {
    item.thumbUrl = facts.thumbUrl;
  }
  if (facts.words !== undefined) {
    item.words = facts.words;
  }
  if (facts.textHash !== undefined) {
    item.textHash = facts.textHash;
  }
}

/** What a paper turned out to say, gathered before any of it is written. */
interface PaperFacts {
  pages?: number;
  thumbHash?: string;
  title?: string;
  publishedAt?: string;
  words?: number;
  failed?: string;
}

/**
 * Everything a PDF says about itself: its title and date, its page count, its
 * word count and page one as the card's picture. A PDF has no `<head>`, so
 * without this a saved paper is a card with a filename on it.
 *
 * `landed` says the bytes came from behind a landing page, whose title a
 * person wrote and which beats the file's own title field.
 */
async function readPaper(
  url: string,
  file: Uint8Array<ArrayBuffer>,
  landed: boolean,
): Promise<PaperFacts> {
  const facts: PaperFacts = {};
  const contents = await readPdf(file);
  if (contents === undefined) {
    // The bytes came back and the engine will not read them: the card is left
    // with what the address gave it, and says so rather than leave the person
    // guessing why the title never arrived.
    facts.failed = "This PDF came back, but could not be read.";
    return facts;
  }
  facts.pages = contents.pages;
  try {
    facts.thumbHash = await putBlob(contents.picture);
  } catch (error) {
    // The picture is decoration: the paper keeps its title and text without
    // it, so a first page that will not store is a card without a picture.
    console.error(
      `Toucan Reads: the first page of ${url} could not be stored`,
      error,
    );
  }
  if (contents.title !== undefined && !landed) {
    facts.title = contents.title;
  }
  if (contents.publishedAt !== undefined) {
    facts.publishedAt = contents.publishedAt;
  }
  if (contents.text !== undefined && contents.text !== "") {
    facts.words = countWords(contents.text);
  }
  return facts;
}

/**
 * The paper behind a landing page, pulled once more from where the page said
 * it was. A file that will not come — over the proxy's cap, or gone — leaves a
 * card without a picture rather than an item without a kind. One that comes
 * back as something other than a PDF, which is what an OJS galley of the HTML
 * or the Word file gives, was never a paper: the page's own verdict stands
 * and the file address is dropped with the claim.
 */
async function paperBehind(facts: PageFacts): Promise<PaperFacts> {
  let file;
  try {
    file = await fetchPage(required(facts.fileUrl, "file url"));
  } catch (error) {
    console.error(
      `Toucan Reads: the file behind ${facts.url} could not be pulled in`,
      error,
    );
    return {};
  }
  if (file.status >= 400) {
    console.error(
      `Toucan Reads: the file behind ${facts.url} answered ${String(file.status)}`,
    );
    return {};
  }
  if (!isPdfContent(file.contentType, file.bytes)) {
    facts.kind = facts.readable ? "article" : "link";
    delete facts.fileUrl;
    return {};
  }
  return readPaper(facts.url, file.bytes, true);
}

/** Write down what a paper said, in one go. */
function writePaper(item: Item, facts: PaperFacts): void {
  item.kind = "pdf";
  if (facts.pages !== undefined) {
    item.pages = facts.pages;
  }
  if (facts.thumbHash !== undefined) {
    item.thumbHash = facts.thumbHash;
  }
  if (facts.title !== undefined) {
    item.title = facts.title;
  }
  // What the landing page said beats the file's own field, so the paper only
  // dates an item nothing has dated yet.
  if (item.publishedAt === undefined) {
    setItemPublished(item, facts.publishedAt);
  }
  if (facts.words !== undefined) {
    item.words = facts.words;
  }
}

/**
 * Find out what one item is, and write it down.
 *
 * `fetchedAt` is stamped only where the server gave a definitive answer,
 * including a refusal — a 429, a 5xx or an unreachable server leaves the
 * question open, so the sweep comes back to it. Returns whether the question
 * was closed.
 */
export async function enrichItem(
  store: ReadsStore,
  item: Item,
  now: number,
): Promise<boolean> {
  // A script fetching a paywalled page gets the teaser and the subscribe form,
  // whose title and social card would replace the headline. The question is
  // closed without asking, so the sweep does not carry it for ever, and the
  // reader gets the snapshot `openUrl` points at.
  if (item.paywalled === true) {
    item.fetchedAt = now;
    return true;
  }
  // The item's own address, even for a paper behind a landing page:
  // `fileUrl` is only what the last fetch concluded, and the page is what
  // says what this is. GitHub's raw host serves a PDF as
  // `application/octet-stream`, which parsed as a page gives the wrong kind.
  const target = item.url;
  // A site that will not answer a script is read through a mirror that will.
  // What comes back is still the page at the address it was saved from.
  const mirror = mirrorOf(target);
  let page;
  try {
    page = await fetchPage(mirror ?? target);
  } catch (error) {
    // The proxy could not answer at all — offline, or a server that does not
    // fetch pages. Nothing was learnt and nothing is recorded; the sweep comes
    // back to it, and the console says which address gave out.
    console.error(`Toucan Reads: could not fetch ${target}`, error);
    return false;
  }
  if (!isSettled(page.status)) {
    return false;
  }
  let refused = false;
  let failed: string | undefined;
  if (page.status >= 400) {
    refused = true;
  } else if (isPdfContent(page.contentType, page.bytes)) {
    // Read and stored in full before anything is written (see `PageFacts`).
    const paper = await readPaper(item.url, page.bytes, false);
    forgetFetched(item);
    writePaper(item, paper);
    failed = paper.failed;
  } else {
    const html = new TextDecoder().decode(page.bytes);
    if (isBotChallenge(html)) {
      // A refusal that came with a 200.
      refused = true;
    } else {
      const facts = await readHtml(
        store,
        html,
        page.url,
        mirror === undefined ? page.url : target,
      );
      // A landing page that fronts a paper: page one of the file is what the
      // card wants, and it too is read before anything is written.
      const paper =
        facts.fileUrl === undefined ? undefined : await paperBehind(facts);
      forgetFetched(item);
      writePage(item, facts);
      failed = facts.failed;
      if (paper !== undefined && facts.kind === "pdf") {
        writePaper(item, paper);
        failed = paper.failed;
      }
    }
  }
  setItemRefused(item, refused);
  setItemFailed(item, failed);
  // Any other refusal is often a site that turns away scripts and not
  // browsers, and a link still opens in one.
  setItemMissing(item, isGone(page.status) ? page.status : undefined, now);
  // Only the fetch can find this out, and only for the item's saved address.
  // A mirror's headers say what the mirror allows.
  setItemUnframeable(item, mirror === undefined && !page.frameable);
  item.fetchedAt = now;
  return true;
}

/** Where each folded-away item went: dropped id → kept id. */
const mergedAway = new Map<string, string>();

/**
 * Where an item went when it turned out to be a second copy of a page already
 * saved. A page left open on the one that was folded away follows it, rather
 * than dropping back to the library as if it had been deleted.
 */
export function mergedInto(id: string): string | undefined {
  return mergedAway.get(id);
}

/**
 * Fold two saves of one page into one, as following redirects and preferring
 * the canonical address can reveal. The older item is kept, taking the
 * newer's list and note where it has none, and the newer is deleted. Returns
 * the id of the item left holding the page, which may not be the one passed
 * in.
 */
export function mergeDuplicate(store: ReadsStore, item: Item): string {
  const twin = store.items.find(
    (other) => other.id !== item.id && other.url === item.url,
  );
  if (twin === undefined) {
    return item.id;
  }
  const older = twin.addedAt <= item.addedAt;
  const keep = older ? twin : item;
  const drop = older ? item : twin;
  if (keep.listId === undefined && drop.listId !== undefined) {
    keep.listId = drop.listId;
  }
  if (keep.note === undefined && drop.note !== undefined) {
    keep.note = drop.note;
  }
  const kept = keep.id;
  mergedAway.set(drop.id, kept);
  store.items.splice(
    store.items.findIndex((other) => other.id === drop.id),
    1,
  );
  return kept;
}

/**
 * Find out what one item is, write it down, and fold it into the item it turns
 * out to be a second copy of. Whether the question is closed — a false
 * says the server never answered, so the item is still work left to do.
 */
export function enrich(
  store: ReadsStore,
  item: Item,
  now: number,
): Promise<boolean> {
  // The sweep can have an item in flight when its edit page opens and asks
  // too, and the second asker waits on the first rather than fetching again.
  const already = inFlight.get(item.id);
  if (already !== undefined) {
    return already;
  }
  const asking = enrichItem(store, item, now)
    .then((settled) => {
      if (settled) {
        // Before the merge, so a second copy's suggested list is carried over
        // the way a list filed by hand is.
        refileItem(store, item);
        mergeDuplicate(store, item);
      }
      return settled;
    })
    .finally(() => {
      inFlight.delete(item.id);
    });
  inFlight.set(item.id, asking);
  return asking;
}

/** The lookups under way, by item, for {@link enrich} to join rather than repeat. */
const inFlight = new Map<string, Promise<boolean>>();
